import { cn } from "@/app/utils/cn";
import { Table, flexRender } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./BasicTable.module.css";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

export type BasicTableProps<T> = {
  getPageCount?: () => number;
  getPageIndex?: () => number;
  handleNextPage?: () => void;
  handlePreviousPage?: () => void;
  handleRowClick?: (item: T, index?: number) => void;
  handleSheetState?: () => void;
  table: Table<T>;
  type: "table with lines" | "only header";
  headerClassName?: string;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  observerRef?: React.RefObject<HTMLDivElement>;
  shouldShowInfiniteScroll?: boolean;
};

export const BasicTable = <T,>({
  getPageCount,
  getPageIndex,
  handleNextPage,
  handlePreviousPage,
  handleRowClick,
  handleSheetState,
  table,
  type,
  headerClassName,
  isLoading,
  isFetchingNextPage,
  observerRef,
  shouldShowInfiniteScroll,
}: BasicTableProps<T>) => {
  const isLineType = type === "table with lines";
  const isOnlyHeaderType = type === "only header";

  return (
    <>
      <div
        className={cn(
          `overflow-hidden ${isLineType && "rounded-xl border border-gray-200 sm:rounded-2xl"}`,
        )}
      >
        <div className="overflow-x-auto">
          <table
            className={`w-full ${isOnlyHeaderType ? styles.onlyHeaderTable : ""}`}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={`h-11 sm:h-12 md:h-14 ${isLineType ? "border-b border-gray-200" : ""}`}
                >
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={cn(
                        `
                        h-11 text-left text-xs font-semibold text-gray-600 sm:h-12 sm:text-sm md:h-14 md:text-base
                        ${
                          isLineType
                            ? `${index === 0 ? "rounded-tl-xl sm:rounded-tl-2xl" : ""} ${index === headerGroup.headers.length - 1 ? "rounded-tr-xl sm:rounded-tr-2xl" : ""}`
                            : ""
                        }
                      `,
                        headerClassName,
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {!isLoading && (
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <tr
                    onClick={() => {
                      if (handleRowClick) {
                        handleRowClick(table.getRow(row.id).original, rowIndex);
                      }
                      if (handleSheetState) {
                        handleSheetState();
                      }
                    }}
                    key={row.id}
                    className={`
                    cursor-pointer hover:bg-gray-50
                    ${isLineType && rowIndex !== table.getRowModel().rows.length - 1 ? "border-b border-gray-200" : ""}
                  `}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={cell.id}
                        className={`
                        h-[54px] text-xs font-medium text-gray-900 sm:h-[64px] sm:text-sm md:h-[72px]
                        ${
                          isLineType
                            ? `
                            ${rowIndex === table.getRowModel().rows.length - 1 && cellIndex === 0 ? "rounded-bl-xl sm:rounded-bl-2xl" : ""}
                            ${rowIndex === table.getRowModel().rows.length - 1 && cellIndex === row.getVisibleCells().length - 1 ? "rounded-br-xl sm:rounded-br-2xl" : ""}
                          `
                            : ""
                        }
                      `}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {observerRef &&
          shouldShowInfiniteScroll &&
          !isFetchingNextPage &&
          table.getRowModel().rows.length > 0 && (
            <div ref={observerRef} className="h-1" />
          )}
      </div>
      {isLoading && (
        <div className="mt-3 flex flex-col gap-2 sm:mt-4">
          {new Array(4).fill(0).map((_, i) => (
            <Skeleton
              key={i}
              className="h-[54px] w-full rounded-lg sm:h-[64px] md:h-[72px]"
            />
          ))}
        </div>
      )}

      {isFetchingNextPage && (
        <div className="mt-3 flex flex-col gap-2 sm:mt-4">
          {new Array(2).fill(0).map((_, i) => (
            <Skeleton
              key={i}
              className="h-[54px] w-full rounded-lg sm:h-[64px] md:h-[72px]"
            />
          ))}
        </div>
      )}

      {getPageIndex && getPageCount && handleNextPage && handlePreviousPage && (
        <div className="mt-4 flex items-center justify-between gap-2 sm:justify-end sm:gap-4">
          <div className="flex items-center gap-1 text-xs text-gray-500 sm:hidden">
            <span>Página</span>
            <span className="font-medium text-gray-900">{getPageIndex()}</span>
            <span>de</span>
            <span className="font-medium text-gray-900">
              {getPageCount() || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={getPageIndex() <= 1}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              <ChevronLeft className="size-3 sm:size-4" />
            </Button>
            <div className="hidden items-center gap-2 text-sm sm:flex">
              <span className="text-gray-500">Página</span>
              <span className="font-medium">{getPageIndex()}</span>
              <span className="text-gray-500">de</span>
              <span className="font-medium">{getPageCount() || 0}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={getPageIndex() >= (getPageCount() || 0)}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              <ChevronRight className="size-3 sm:size-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
